import {
  ref, computed, watch, onUnmounted,
} from 'vue';
import { storeToRefs } from 'pinia';
import { usePCDStore } from '@/stores/usePCDStore.js';
import {
  Color, BufferAttribute, CanvasTexture, PointsMaterial,
} from 'three';
import { PCDLoader } from 'three/addons/loaders/PCDLoader.js';
import * as THREE from 'three';

export default function usePCD() {
  const {
    pointArray,
    pcdAnnotations,
    header,
    fields,
    scale,
    enablePCD,
  } = storeToRefs(usePCDStore());
  const {
    $reset,
  } = usePCDStore();

  onUnmounted(() => {
    $reset();
  });

  const SPHERE_TEXTURE = createSphereTexture();
  // const POINTS_MATERIAL = createPointsMaterial();

  function createSphereTexture() {
    const tempcanvas = document.createElement('canvas');
    tempcanvas.width = 64;
    tempcanvas.height = 64;
    const ctx = tempcanvas.getContext('2d');

    // Clear the canvas to make it fully transparent
    ctx.clearRect(0, 0, tempcanvas.width, tempcanvas.height);

    // Draw a circle
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(32, 32, 30, 0, Math.PI * 2);
    ctx.fill();
    return new CanvasTexture(tempcanvas);
  }

  function createPointsMaterial() {
    const pointsMaterial = new PointsMaterial();
    pointsMaterial.map = SPHERE_TEXTURE;
    pointsMaterial.transparent = true;
    pointsMaterial.size = 0.25;
    // pointsMaterial.value.sizeAttenuation = false;
    pointsMaterial.alphaTest = 0.5;
    // pointsMaterial.value.depthWrite = false;
    // pointsMaterial.value.blending = THREE.NormalBlending;
    pointsMaterial.needsUpdate = true;
    return pointsMaterial;
  }

  function setPointsMaterial(pointsMaterial) {
    pointsMaterial.map = SPHERE_TEXTURE;
    pointsMaterial.transparent = true;
    pointsMaterial.size = 0.25;
    pointsMaterial.sizeAttenuation = true;
    pointsMaterial.alphaTest = 0.5;
    // pointsMaterial.value.depthWrite = false;
    // pointsMaterial.value.blending = THREE.NormalBlending;
    pointsMaterial.color.set(0xFFCC00);
    pointsMaterial.needsUpdate = true;
  }

  function parseFields(line) {
    const lineSplit = line.split(' ');
    if (lineSplit.length > 2) {
      lineSplit.shift();
      fields.value = lineSplit;
    }
  }

  // function createTestBufferGeometry(positions, count) {
  //   const geometry = new BufferGeometry();
  //   // Populate the position data
  //   for (let i = 0; i < count; i++) {
  //     positions[i * 3] = Math.random() * 100; // x
  //     positions[i * 3 + 1] = Math.random() * 100; // y
  //     positions[i * 3 + 2] = Math.random() * 100; // z
  //   }

  //   geometry.setAttribute('position', new BufferAttribute(positions, 3));
  //   return geometry;
  // }

  function parsePoints(data) {
    const points = [];

    const labelIndex = fields.value.indexOf('label');

    data.forEach((line) => {
      const lineSplit = line.split(' ');
      if (lineSplit.length > 2) {
        const px = parseFloat(lineSplit[0]);
        if (Number.isNaN(px)) return;
        const py = parseFloat(lineSplit[1]);
        if (Number.isNaN(py)) return;
        const pz = parseFloat(lineSplit[2]);
        if (Number.isNaN(pz)) return;

        const label = parseInt(lineSplit[labelIndex]);

        points.push({
          raw: lineSplit,
          position: [px * scale.value, py * scale.value, pz * scale.value],
          label,
          selected: false,
        });
      }
    });
    return points;
  }

  function jetColormap(val) {
    const color = new Color();

    const t = Math.max(0, Math.min(1, val)); // Clamp between 0 and 1

    if (t < 0.25) {
      // Blue to Cyan
      color.setRGB(0, 4 * t, 1);
    } else if (t < 0.5) {
      // Cyan to Green
      color.setRGB(0, 1, 1 - 4 * (t - 0.25));
    } else if (t < 0.75) {
      // Green to Yellow
      color.setRGB(4 * (t - 0.5), 1, 0);
    } else {
      // Yellow to Red
      color.setRGB(1, 1 - 4 * (t - 0.75), 0);
    }

    return color;
  }

  function parsePCDFileAsPointsBuffer(arrayBuffer) {
    const loader = new PCDLoader();
    const points = loader.parse(arrayBuffer, 'blob.pcd'); // Parse buffer
    points.material.size = 3;
    points.material.sizeAttenuation = false;

    return points;
  }

  function colorPointsByXValue(points) {
    const geometry = points.geometry; // Extract geometry from Points

    // Get position attribute (3 values per vertex: x, y, z)
    const positionAttribute = geometry.attributes.position;
    const numPoints = positionAttribute.count; // Total number of points

    // Collect all x-values
    const xValues = [];
    for (let i = 0; i < numPoints; i++) {
      xValues.push(positionAttribute.getX(i));
    }

    // Sort values to find percentiles
    xValues.sort((a, b) => a - b);

    // Define lower and upper percentile thresholds
    const lowerPercentile = 0.05; // Ignore bottom 5%
    const upperPercentile = 0.95; // Ignore top 5%

    // Compute min and max based on percentiles
    const minX = xValues[Math.floor(lowerPercentile * numPoints)];
    const maxX = xValues[Math.floor(upperPercentile * numPoints)];
    const rangeX = maxX - minX;

    // Create color buffer
    const colors = new Float32Array(numPoints * 3); // RGB for each point

    for (let i = 0; i < numPoints; i++) {
      let x = positionAttribute.getX(i);

      // Clamp X within the computed min-max range
      x = Math.max(minX, Math.min(maxX, x));

      // Normalize X between 0 and 1
      const normalizedX = (x - minX) / rangeX;

      // Convert normalizedX to a Jet colormap (blue → red)
      const color = jetColormap(normalizedX);

      colors[i * 3] = color.r; // Red
      colors[i * 3 + 1] = color.g; // Green
      colors[i * 3 + 2] = color.b; // Blue
    }

    // Assign colors to geometry
    geometry.setAttribute('color', new BufferAttribute(colors, 3));

    // Enable vertex colors
    points.material.vertexColors = true;
  }

  function getPointsColorByXValue(positionAttribute) {
    const numPoints = positionAttribute.count; // Total number of points

    // Collect all x-values
    const xValues = [];
    for (let i = 0; i < numPoints; i++) {
      xValues.push(positionAttribute.getX(i));
    }

    // Sort values to find percentiles
    xValues.sort((a, b) => a - b);

    // Define lower and upper percentile thresholds
    const lowerPercentile = 0.05; // Ignore bottom 5%
    const upperPercentile = 0.95; // Ignore top 5%

    // Compute min and max based on percentiles
    const minX = xValues[Math.floor(lowerPercentile * numPoints)];
    const maxX = xValues[Math.floor(upperPercentile * numPoints)];
    const rangeX = maxX - minX;

    // Create color buffer
    const colors = []; // RGB for each point

    for (let i = 0; i < numPoints; i++) {
      let x = positionAttribute.getX(i);

      // Clamp X within the computed min-max range
      x = Math.max(minX, Math.min(maxX, x));

      // Normalize X between 0 and 1
      const normalizedX = (x - minX) / rangeX;

      // Convert normalizedX to a Jet colormap (blue → red)
      const color = jetColormap(normalizedX);

      colors.push(color);
    }

    return colors;
  }

  function parsePCDFile(pcdFile) {
    const lines = pcdFile.split(/\r?\n/);

    header.value = lines.slice(0, 11);
    const data = lines.slice(11);

    header.value.forEach((line) => {
      if (line.startsWith('FIELDS')) {
        parseFields(line);
      }
    });

    const points = parsePoints(data);

    return points;
  }

  // TODO: This is no longer valid after switching to buffer geometry
  // function createPCDFile() {
  //   const labelIndex = fields.value.indexOf('label');

  //   let newPCDFile = header.value.join('\n');
  //   newPCDFile += '\n';

  //   pointArray.value.forEach((point) => {
  //     const newPoint = point.raw;
  //     newPoint[0] = point.position[0] / scale.value;
  //     newPoint[1] = point.position[1] / scale.value;
  //     newPoint[2] = point.position[2] / scale.value;
  //     newPoint[labelIndex] = point.label;
  //     newPCDFile += `${newPoint.join(' ')}\n`;
  //   });

  //   return newPCDFile;
  // }

  // Function to create a fresh points buffer from raw data. Needed because tres.js clears the geometry when disposed
  function cloneLidarPointsBuffer(positions, colors) {
  // Create a new geometry from the original data
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

    // Compute bounds
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();

    // Create material
    const material = new THREE.PointsMaterial({
      size: 0.1,
      vertexColors: true,
    });

    // Create new points object
    const points = new THREE.Points(geometry, material);
    return points;
  }

  // Function to create a fresh points buffer from raw data. Needed because tres.js clears the geometry when disposed
  function clonePcdPointsBuffer(positions, colors) {
  // Create a new geometry from the original data
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

    // Compute bounds
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();

    // Create material
    const material = new THREE.PointsMaterial();
    setPointsMaterial(material);

    // Create new points object
    const points = new THREE.Points(geometry, material);
    return points;
  }

  return {
    pointArray,
    pcdAnnotations,
    header,
    fields,
    scale,
    enablePCD,
    // setPointsMaterial,
    // createPointsMaterial,
    // createTestBufferGeometry,
    parsePCDFile,
    parsePCDFileAsPointsBuffer,
    colorPointsByXValue,
    getPointsColorByXValue,
    setPointsMaterial,
    // createPCDFile,
    cloneLidarPointsBuffer,
    clonePcdPointsBuffer,
  };
}
